import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchVideoProject } from '@/api';

interface VideoProject {
  id: string;
  title: string;
  description: string;
  status: 'draft' | 'scripting' | 'production' | 'review' | 'published';
}

export default function VoiceStudio() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [project, setProject] = useState<VideoProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadProject();
    }
  }, [id]);

  const loadProject = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const projectData = await fetchVideoProject(id);
      setProject(projectData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-600">Loading voice studio...</span>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Project Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The requested project could not be loaded.'}</p>
          <button
            onClick={() => navigate('/projects')}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate(`/projects/${project.id}`)}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Back to Project"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{project.title}</h1>
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                Voice Studio
              </span>
            </div>
            <p className="text-gray-600">{project.description}</p>
          </div>
        </div>
      </div>

      {/* Voice Recording Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-3">🎤 Voice Studio</h2>
          <p className="text-gray-600 mb-8 max-w-lg mx-auto">
            Record your voiceover for the script. This step is coming soon! You'll be able to record high-quality audio directly from your browser.
          </p>
          
          <div className="inline-flex gap-3">
            <button
              onClick={() => navigate(`/projects/${project.id}`)}
              className="px-6 py-3 bg-gray-100 text-gray-900 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              ← Back to Script
            </button>
            <button
              onClick={() => navigate('/projects')}
              className="px-6 py-3 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 transition-colors font-medium"
            >
              View All Projects
            </button>
          </div>

          {/* Feature preview */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Coming Soon</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto text-left">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="w-8 h-8 bg-blue-200 rounded-lg flex items-center justify-center mb-3">
                  🎙️
                </div>
                <h4 className="font-medium text-gray-900 mb-1">Record Audio</h4>
                <p className="text-sm text-gray-600">Record high-quality voiceover directly in your browser</p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="w-8 h-8 bg-green-200 rounded-lg flex items-center justify-center mb-3">
                  ✂️
                </div>
                <h4 className="font-medium text-gray-900 mb-1">Edit & Trim</h4>
                <p className="text-sm text-gray-600">Cut, trim, and adjust timing for perfect pacing</p>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="w-8 h-8 bg-purple-200 rounded-lg flex items-center justify-center mb-3">
                  🎵
                </div>
                <h4 className="font-medium text-gray-900 mb-1">Add Music</h4>
                <p className="text-sm text-gray-600">Layer in royalty-free background music</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
